'use client'

import { memo, useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Apple, AppWindow, ChevronDown, CheckCircle2 } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type DownloadPlatform = 'mac-arm64' | 'mac-x64' | 'win-x64'

export interface PlatformFlags {
  macArm64: boolean
  macX64: boolean
  winX64: boolean
  version?: string
}

async function fetchPlatforms(apiBase: string): Promise<PlatformFlags> {
  const res = await fetch(`${apiBase}?format=json`)
  if (!res.ok) return { macArm64: false, macX64: false, winX64: false }
  return res.json() as Promise<PlatformFlags>
}

export interface ProductDownloadSectionProps {
  productLabel: string
  productDescription?: string
  apiBase: string
  queryKey: string[]
  recommended?: boolean
  variant?: 'primary' | 'secondary'
  /** inline = hero-style buttons only; card = bordered product block */
  layout?: 'card' | 'inline' | 'editorial'
}

export const ProductDownloadSection = memo(function ProductDownloadSection({
  productLabel,
  productDescription,
  apiBase,
  queryKey,
  recommended = false,
  variant = 'primary',
  layout = 'card',
}: ProductDownloadSectionProps) {
  const [downloadSuccess, setDownloadSuccess] = useState<DownloadPlatform | null>(null)

  const { data: platforms, isPending, isError } = useQuery({
    queryKey,
    queryFn: () => fetchPlatforms(apiBase),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })

  const handleDownload = useCallback(
    (platform: DownloadPlatform) => {
      window.open(`${apiBase}?platform=${platform}`, '_blank', 'noopener,noreferrer')
      setDownloadSuccess(platform)
      window.setTimeout(() => setDownloadSuccess(null), 3000)
    },
    [apiBase]
  )

  const isPrimary = variant === 'primary'
  const isInline = layout === 'inline'

  if (layout === 'editorial') {
    const choices = [
      { id: 'mac-arm64' as const, title: 'Mac', detail: 'Apple Silicon · M-series', available: platforms?.macArm64, Icon: Apple },
      { id: 'mac-x64' as const, title: 'Mac', detail: 'Intel processor', available: platforms?.macX64, Icon: Apple },
      { id: 'win-x64' as const, title: 'Windows', detail: '64-bit desktop', available: platforms?.winX64, Icon: AppWindow },
    ]
    return (
      <div className="nx-download-panel">
        <div className="nx-download-panel-heading"><span className="nx-eyebrow">NINX / DESKTOP</span>{platforms?.version ? <span className="nx-download-version">v{platforms.version.replace(/^v/, '')}</span> : null}</div>
        <h3>Choose your machine.</h3>
        <p className="nx-download-intro">Pick your processor below. On a Mac, check Apple menu → About This Mac.</p>
        <div className="nx-download-options">
          {choices.map(({ id, title, detail, available, Icon }) => (
            <button key={id} type="button" className="nx-download-option" disabled={isPending || isError || !available} onClick={() => handleDownload(id)} aria-label={`Download NINX for ${title}, ${detail}`}>
              <Icon size={24} aria-hidden="true" />
              <span className="nx-download-platform"><strong>{title}</strong><span>{detail}</span></span>
              <span className="nx-download-availability">{isPending ? 'Checking…' : isError ? 'Unavailable' : available ? 'Download ↓' : 'Coming soon'}</span>
            </button>
          ))}
        </div>
        <p className="nx-download-footnote">Free to start. Local models run on your device.</p>
        {downloadSuccess ? <p className="nx-download-status" role="status"><CheckCircle2 size={16} aria-hidden="true" /> Download requested. Check your browser’s downloads.</p> : null}
      </div>
    )
  }

  const downloadButtons = (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-between gap-3 rounded-xl px-6 py-4 transition hover:-translate-y-0.5 min-w-[200px]',
                isPrimary
                  ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20 hover:bg-slate-800'
                  : 'bg-white border border-slate-200 text-slate-900 shadow-sm hover:bg-slate-50'
              )}
            >
              <div className="flex items-center gap-3">
                <Apple className="h-6 w-6" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-none uppercase tracking-wider opacity-70">
                    Download for
                  </span>
                  <span className="text-base font-bold leading-tight">macOS</span>
                </div>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2 bg-white border-slate-200 shadow-lg" sideOffset={8}>
            <div className="flex flex-col gap-1">
              {platforms?.macArm64 ? (
                <button
                  type="button"
                  className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left transition w-full hover:bg-slate-100 text-slate-900 cursor-pointer"
                  onClick={() => handleDownload('mac-arm64')}
                >
                  <span className="text-sm font-semibold text-slate-900">Mac Silicon</span>
                  <span className="text-xs text-slate-600">M1, M2, M3, M4 chips</span>
                </button>
              ) : (
                <div className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left">
                  <span className="text-sm font-semibold text-slate-700">Mac Silicon</span>
                  <span className="text-xs text-slate-600">Temporarily unavailable</span>
                </div>
              )}
              {platforms?.macX64 ? (
                <button
                  type="button"
                  className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left transition hover:bg-slate-100 text-slate-900 w-full cursor-pointer"
                  onClick={() => handleDownload('mac-x64')}
                >
                  <span className="text-sm font-semibold">Mac Intel</span>
                  <span className="text-xs text-slate-500">Intel chips</span>
                </button>
              ) : (
                <div className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left opacity-60">
                  <span className="text-sm font-semibold text-slate-600">Mac Intel</span>
                  <span className="text-xs text-slate-500">Coming soon</span>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {platforms?.winX64 ? (
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-3 rounded-xl px-6 py-4 transition hover:-translate-y-0.5 min-w-[200px]',
              isPrimary
                ? 'bg-white border border-slate-200 text-slate-900 shadow-sm hover:bg-slate-50'
                : 'bg-white border border-slate-200 text-slate-900 shadow-sm hover:bg-slate-50'
            )}
            onClick={() => handleDownload('win-x64')}
          >
            <AppWindow className="h-6 w-6" />
            <div className="flex flex-col items-start">
              <span className="text-[10px] leading-none text-slate-400 uppercase tracking-wider">Download for</span>
              <span className="text-base font-bold leading-tight">Windows</span>
            </div>
          </button>
        ) : (
          <div className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-6 py-4 text-slate-400 min-w-[200px] cursor-not-allowed">
            <AppWindow className="h-6 w-6" />
            <div className="flex flex-col items-start">
              <span className="text-[10px] leading-none uppercase tracking-wider">Coming soon</span>
              <span className="text-base font-bold leading-tight">Windows</span>
            </div>
          </div>
        )}
    </div>
  )

  if (isInline) {
    return (
      <div className="flex flex-col gap-4 items-center">
        {downloadSuccess ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
            role="status"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Download started. Check your Downloads folder.
          </div>
        ) : null}
        {downloadButtons}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-2xl border p-6',
        isPrimary
          ? 'border-[#21728d]/30 bg-white shadow-lg shadow-[#21728d]/10'
          : 'border-slate-200 bg-slate-50/80'
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className={cn('font-bold text-slate-900', isPrimary ? 'text-xl' : 'text-lg')}>
          {productLabel}
        </h3>
        {recommended ? (
          <span className="rounded-full bg-[#21728d] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            Recommended
          </span>
        ) : null}
      </div>
      {productDescription ? <p className="text-sm text-slate-500">{productDescription}</p> : null}
      {downloadSuccess ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          role="status"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Download started. Check your Downloads folder.
        </div>
      ) : null}
      {downloadButtons}
    </div>
  )
})
